import { GoogleLogin } from "@react-oauth/google";
import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { api, type AuthResponse } from "../api";
import { setSessionToken } from "../auth";

export function LoginPage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState("");
  const [magicBusy, setMagicBusy] = useState(false);
  const [magicSent, setMagicSent] = useState(false);

  async function handleMagicLink(e: FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setMagicBusy(true);
    setError(null);
    try {
      await api<{ message: string }>("/v1/auth/magic-link/request", {
        method: "POST",
        body: JSON.stringify({ email: email.trim() }),
      });
      setMagicSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't send the link.");
    } finally {
      setMagicBusy(false);
    }
  }

  async function handleCredential(idToken: string) {
    setBusy(true);
    setError(null);
    try {
      const res = await api<AuthResponse>("/v1/auth/google", {
        method: "POST",
        body: JSON.stringify({ idToken }),
      });
      setSessionToken(res.sessionToken);
      navigate("/", { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Sign-in failed");
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <div className="w-full max-w-sm text-center space-y-6">
        <div>
          <img
            src="/logo-680.png"
            alt="680 Drivers Water Polo"
            className="mx-auto h-16 w-auto"
          />
          <h1 className="mt-5 text-2xl font-semibold tracking-tight text-brand-700">
            Club Manager
          </h1>
          <p className="mt-2 text-sm text-slate-600">Sign in to view your team's games.</p>
        </div>

        <div className="flex justify-center">
          {busy ? (
            <span className="text-sm text-slate-500">Signing in…</span>
          ) : (
            <GoogleLogin
              onSuccess={(cred) => {
                if (cred.credential) void handleCredential(cred.credential);
                else setError("No credential returned from Google");
              }}
              onError={() => setError("Google sign-in was cancelled or failed")}
              useOneTap={false}
            />
          )}
        </div>

        <div className="flex items-center gap-3">
          <div className="h-px flex-1 bg-slate-200" />
          <span className="text-xs uppercase tracking-wide text-slate-400">or</span>
          <div className="h-px flex-1 bg-slate-200" />
        </div>

        {magicSent ? (
          <p className="text-sm text-slate-600">
            If <span className="font-medium">{email.trim()}</span> is registered as a coach, a
            sign-in link is on its way. Check your inbox — the link works once and expires in
            24&nbsp;hours.
          </p>
        ) : (
          <form onSubmit={handleMagicLink} className="space-y-3 text-left">
            <label className="block text-sm text-slate-600">
              No Google account? Get a sign-in link by email.
              <input
                type="email"
                required
                autoComplete="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </label>
            <button
              type="submit"
              disabled={magicBusy}
              className="w-full rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
            >
              {magicBusy ? "Sending…" : "Email me a sign-in link"}
            </button>
          </form>
        )}

        {error && <p className="text-sm text-rose-600">{error}</p>}
      </div>
    </div>
  );
}
